/**
 * Ticket-template gallery — a static, read-only preview of every code-level
 * rendering variant (`editorial`, `stencil`, `strata`) an event or ticket
 * type may pick as its own `templateVariant`. There is no template catalog to
 * browse, publish, or manage anymore — a "template" IS the enum value
 * itself (@momo/types' TicketTemplateVariantSchema), stored directly on the
 * event/ticket-type doc and resolved by resolveTicketTemplateVariant
 * (ticket-type.entity.ts) — so this view carries no Firestore read and no
 * write: it iterates TICKET_TEMPLATE_VARIANTS and mounts each as a
 * <ticket-template-preview>, the same tag ticket-template-picker.view.ts's
 * rows mount, just at a larger, legible display width — a showcase, not a
 * compact picker. That tag draws the OPERATOR's own brand
 * (preview.state.ts), so the gallery shows the tile a signed-in admin's
 * tickets actually mint, not an invented studio's.
 * Each tile's label line carries two neutral outlined
 * icon-buttons (never the vendors' reserved "Add to Wallet" badge artwork)
 * opening a `show()` overlay with the schematic of the wallet pass that
 * variant produces (<apple-pass-schematic> / <google-pass-schematic>).
 */
import { tooltip } from "@mhmo91/schmancy";
import { fill, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { TICKET_TEMPLATE_VARIANTS } from "@momo/ticket-template";
import type { TicketTemplateVariant } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
// Side-effect: registers <apple-pass-schematic>.
import "./apple-pass-schematic.view.ts";
// Side-effect: registers <google-pass-schematic>.
import "./google-pass-schematic.view.ts";
// Side-effect: registers <ticket-template-preview>.
import "./ticket-template-preview.view.ts";

const GALLERY_THUMB_WIDTH = 280;

@schmancyElement("ticket-template-catalog")
export class TicketTemplateCatalog extends LocalizeMixin(SchmancyElement) {
	/** A factory template, not an eager one: it is called at mount time, so
	 *  the overlay's inputs read the trigger's current values rather than a
	 *  snapshot frozen where this template was written. */
	private openPreview(
		platform: "apple" | "google",
		variant: TicketTemplateVariant,
	): void {
		show(
			() =>
				platform === "apple"
					? html`<apple-pass-schematic .variant=${variant}></apple-pass-schematic>`
					: html`<google-pass-schematic .variant=${variant}></google-pass-schematic>`,
			{
				ariaLabel: this.t(
					`admin.ticketTemplates.walletPreview.${platform}OverlayAriaLabel`,
					{ variant: this.t(`admin.ticketTemplates.variant.${variant}`) },
				),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override render(): TemplateResult {
		return html`
			<section ${fill()} ${overflowWithin()} class="flex flex-col">
				<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 p-4 md:p-6 lg:p-8">
					${repeat(
						TICKET_TEMPLATE_VARIANTS,
						(variant) => variant,
						(variant) => {
							const variantLabel = this.t(
								`admin.ticketTemplates.variant.${variant}`,
							);
							const appleLabel = this.t(
								"admin.ticketTemplates.walletPreview.appleTriggerLabel",
								{ variant: variantLabel },
							);
							const googleLabel = this.t(
								"admin.ticketTemplates.walletPreview.googleTriggerLabel",
								{ variant: variantLabel },
							);
							return html`
								<div class="flex flex-col items-center gap-3">
									<ticket-template-preview
											.variant=${variant}
											.width=${GALLERY_THUMB_WIDTH}
										></ticket-template-preview>
									<div class="flex items-center gap-2">
										<schmancy-typography type="title" token="sm">
											${variantLabel}
										</schmancy-typography>
										<schmancy-icon-button
											icon="ios"
											size="sm"
											variant="outlined"
											color="neutral"
											aria-label=${appleLabel}
											${tooltip(appleLabel)}
											@click=${() => this.openPreview("apple", variant)}
										></schmancy-icon-button>
										<schmancy-icon-button
											icon="android"
											size="sm"
											variant="outlined"
											color="neutral"
											aria-label=${googleLabel}
											${tooltip(googleLabel)}
											@click=${() => this.openPreview("google", variant)}
										></schmancy-icon-button>
									</div>
								</div>
							`;
						},
					)}
				</div>
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-template-catalog": TicketTemplateCatalog;
	}
}
